import * as React from 'react';
import styled from "styled-components";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import {
  getPermitDocuments,
  downloadAllPermitClientDocuments,
  downloadSinglePermitDocumentWithFullName,
} from '../../api/projectDocuments';
import Table from '../../organisms/Table';
import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import {
    ButtonWithLoader,
} from '../../molecules';
import { convertTableBoolean } from '../../utils/convertTableValues';
import { getVocabulariesDataList } from '../../api/vocabulary';
import { GET_ALL_QUERY_PARAMS } from '../../constants';
import openInNewTab from '../../utils/openInNewTab';
import ReviewerCommentForm1 from '../../forms/ReviewerCommentForm/ReviewerCommentForm1';
import DocumentsDetailsFileList from './DocumentsDetailsFileList';
import useUserRole from '../../hooks/useUserRole';

type ReviewerComment = {
    reviewerComment: string;
    reference: string;
    status: number;
}
const labels = {
    reviewerComment: 'Reviewer Comments',
    status: 'Status',
    reference: 'Reference',
  };

const schema = yup.object().shape({
    reviewerComment: yup.string().label(labels.reviewerComment),
    status: yup.object().label(labels.status),
    reference: yup
      .string()

      .label(labels.reference),
  });

const DocumentsDetailedPage = ({
  onSubmit,
  row,
  work,
  building,
  setAddDocument,
  setDocsAvailable,
  setDownloadAllDocs,
  setDocsWaitingOnClient,
  isViewMode = false,
  initialValues,
}: Props) => {
    const {
        handleSubmit,
        formState,
        errors,
        control,
        setValue,
        register,
        } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { test: [] },
    });

  const { isEmployee } = useUserRole();
  const [documents, setDocuments] = React.useState([]);
  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors]);

  const inspectorGetPermitDocuments = params => {
    return Promise.all([
      getPermitDocuments(params, row?.permitId?.toString()),
      getVocabulariesDataList({
        ...GET_ALL_QUERY_PARAMS,
        vocabularyEntity: 10,
      }),
    ]).then(([documentsResponse, statusesResponse]) => {
      if (documentsResponse.data.results?.length) {
        setDocsAvailable(true);
        setDocuments(documentsResponse.data.results);
      }
      return {
        ...documentsResponse,
        data: {
          ...documentsResponse.data,
            results: initialValues?.selectedDocuments?.map(
            document => {
              const currentStatus = statusesResponse.data.results?.find(
                el => el?.id === document.status,
              );
              if (
                currentStatus?.title === 'Waiting on client'
              ) {
                setDocsWaitingOnClient(true);
              }
              return {
                name: document?.name,
                clientFilesDetails: document?.clientFiles?.map(el => {
                  return { fileName: el?.fileName, url: el?.file, fileId: el?.id };
                }),
                clientFiles: document?.clientFiles?.map(el => {
                  return el?.fileName || el?.file;
                }),
                pk: document.pk,
                documentFile: document?.clientFiles?.map(el => el?.file),
                status: currentStatus?.title,
                submitted:
                  currentStatus?.title === 'Submitted',
              };
            },
          ),
        },
      };
    });
  };

  const columnsForInspector = column => {
    if (
      [
        'pk',
        'permit',
        'jurisdiction',
        'work',
        'building',
        'adminFile',
        'clientFile',
        'submitted',
        'lastComment',
        'settingDoc',
        'isCompleted',
        'documentFile',
        'isForClient',
        'clientFilesDetails',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'name':
        return 'Document Name';
      case 'clientFiles':
        return 'File';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const columnsOrderForInspector = [
    'name',
    // 'adminFileName',
    // 'clientFileName',
    'clientFiles',
    'status',
  ];

  const cellCallbackFnIsOn = [
    // 'adminFileName',
    // 'clientFileName',
    'clientFiles',
  ];

  const downloadDocuments = response => {
    const byteCharacters = atob(response.data.file);
    const type = response.data.contentType.split(";")[0];
    const { fileName } = response.data;
    const byteNumbers = new Array(byteCharacters.length);
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type,
    });
    saveAs(blob, fileName);
  };

    const handleDownloadAllDocs = () => {
      const docPk = initialValues?.selectedDocuments?.map(el => el?.pk);
      Promise.all([downloadAllPermitClientDocuments({ docPk, client: true })])
        .then(([response]) => {
          downloadDocuments(response);
        })
        .catch(() => {});
    };

    const handleCellClick = async (rowValue, clickedColumn) => {
      const _data = {
        apiId: 'client_file_id',
        fileId: rowValue?.clientFilesDetails[0]?.fileId,
        documentPk: rowValue.pk,
      };

      const res = await downloadSinglePermitDocumentWithFullName(_data);
      const base64 = res.data.file;
      const newTab = window.open("");
      newTab.document.write(`<iframe width="100%" height="100%" src="data:application/pdf;base64,${base64}"></iframe>`);
    };

  return (
    <Container>
      <StyledDocumentsSection>
        <ButtonWithLoader
          disabled={isSubmitting || !initialValues?.selectedDocuments?.length}
          loading={false}
          onClick={() => handleDownloadAllDocs()}
        >Download All
        </ButtonWithLoader>
        <br />
        {!isEmployee && <DocumentsDetailsFileList row={row} inspectorGetPermitDocuments={inspectorGetPermitDocuments} />}
        { isEmployee && <Table
          customSearch
          isMultipleDocumentsClientFiles
          useViewModeInsteadEdit
          rows={inspectorGetPermitDocuments}
          columns={columnsForInspector}
          columnsOrder={columnsOrderForInspector}
          formatValue={convertTableBoolean}
          onEdit={null}
          onSelect={null}
          onConfigure={null}
          onDelete={null}
          additionalActions={null}
          cellCallbackFnIsOn={cellCallbackFnIsOn}
          handleCellClickClientFile={handleCellClick}
          handleCellClick={handleCellClick}
          docs
        />}
      </StyledDocumentsSection>
      <StyledReviewComments>
        <ReviewerCommentForm1 reviewId={initialValues?.id} values={initialValues} />
      </StyledReviewComments>
    </Container>
  );
};

const Container = styled.fieldset`
  display: flex;
  gap: 20px;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: left;
  box-sizing: border-box;

  @media only screen and (max-width: 840px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const FormContainer = styled.form`
  width: 100%;
  display:flex;
  justify-content: space-evenly;
  .MuiButton-root {
    margin-top: 40px;
    margin-left: 10px;
  }  
`;

const StyledDocumentsSection = styled.div`
    flex: 0 0 38%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    box-sizing: border-box;

    @media only screen and (max-width: 840px) {
      flex: 1 1 auto;
      width: 100%;
    }
`;

const StyledReviewComments = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    padding-left: 20px;
    border-left: 1px solid var(--border);
    box-sizing: border-box;

    @media only screen and (max-width: 840px) {
      padding-left: 0;
      border-left: 0;
      border-top: 1px solid var(--border);
      padding-top: 16px;
      width: 100%;
    }
`;

interface Props {
  row: any;
  work?: number;
  building?: number;
  isAddDocument: boolean;
  isSendDocs: boolean;
  setAddDocument: (data: boolean) => void;
  isDownloadAllDocs: boolean;
  setDocsAvailable: (data: boolean) => void;
  setDownloadAllDocs: (data: boolean) => void;
  setDocsWaitingOnClient: (data: boolean) => void;
  setSendDocs: (data: boolean) => void;
  isInspector: boolean;
  isViewMode?: boolean;
  initialValues?: any;
  cameFromSearch?:boolean;
  handleSubmit?: any;
  onSubmit: (data) => void;
  control?: any
}

export default DocumentsDetailedPage;
